import React, { useState } from "react";

const SECTION = { marginBottom: 20 };
const SECTION_TITLE = { fontSize: 11, fontWeight: 700, color: "#60a5fa", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 };
const DIVIDER = { borderTop: "1px solid #1e2d45", margin: "16px 0" };
const SUB_LABEL = { fontSize: 10, color: "#8a9bb0", marginBottom: 6 };

function NumInput({ label, value, onChange, max = 6 }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 3 }}>{label}</div>
      <input
        type="number" min="0" max={max} value={value}
        onChange={e => onChange(Math.max(0, Math.min(max, parseInt(e.target.value, 10) || 0)))}
        style={{
          width: "100%", textAlign: "center",
          background: "#0f172a", border: "1px solid #334155",
          color: "#e2e8f0", borderRadius: 5,
          padding: "4px 0", fontSize: 12, outline: "none",
        }}
      />
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, cursor: "pointer", userSelect: "none" }}
      onClick={() => onChange(!value)}
    >
      <div style={{
        width: 34, height: 18, borderRadius: 10, flexShrink: 0,
        background: value ? "#2563eb" : "#334155",
        position: "relative", transition: "background 0.2s",
      }}>
        <div style={{
          position: "absolute", top: 2,
          left: value ? 18 : 2, width: 14, height: 14,
          borderRadius: "50%", background: "#fff", transition: "left 0.2s",
        }} />
      </div>
      <span style={{ fontSize: 12, color: value ? "#93c5fd" : "#8a9bb0" }}>{label}</span>
    </div>
  );
}

// ── Output Sets sub-component ─────────────────────────────────────────────────
function OutputSetsSection({ outputSets, onChangeOutputSets, separateExcelPerOutput, onChangeSeparate }) {
  const [expandedIdx, setExpandedIdx] = useState(null);

  const addSet = () => {
    const newSet = { name: `Output ${outputSets.length + 1}`, keys: [], enabled: true };
    onChangeOutputSets([...outputSets, newSet]);
    setExpandedIdx(outputSets.length);
  };

  const removeSet = (i) => {
    const next = outputSets.filter((_, idx) => idx !== i);
    onChangeOutputSets(next);
    setExpandedIdx(null);
  };

  const updateSet = (i, field, value) => {
    const next = outputSets.map((s, idx) => idx === i ? { ...s, [field]: value } : s);
    onChangeOutputSets(next);
  };

  const parseKeys = (raw) =>
    raw.split(/[\n,;\s]+/).map(k => k.trim()).filter(Boolean);

  const keysToText = (keys) => (keys || []).join(", ");

  return (
    <div style={SECTION}>
      <div style={SECTION_TITLE}>Output Sets</div>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10 }}>
        Define named groups of question keys. Each enabled set produces its own table of contents + data sheet.
        When no sets are enabled, all questions are exported (current default behaviour).
      </div>

      {outputSets.map((s, i) => (
        <div key={i} style={{
          background: "#0c1928", border: "1px solid #1e3a5f",
          borderRadius: 8, marginBottom: 8, overflow: "hidden",
        }}>
          {/* Header row */}
          <div style={{
            display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
            cursor: "pointer",
          }} onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}>
            {/* Enable toggle */}
            <div
              onClick={e => { e.stopPropagation(); updateSet(i, 'enabled', !s.enabled); }}
              style={{
                width: 28, height: 16, borderRadius: 8, flexShrink: 0,
                background: s.enabled ? "#2563eb" : "#334155",
                position: "relative", cursor: "pointer", transition: "background 0.2s",
              }}
            >
              <div style={{
                position: "absolute", top: 2,
                left: s.enabled ? 14 : 2, width: 12, height: 12,
                borderRadius: "50%", background: "#fff", transition: "left 0.2s",
              }} />
            </div>

            {/* Name */}
            <input
              value={s.name}
              onClick={e => e.stopPropagation()}
              onChange={e => updateSet(i, 'name', e.target.value)}
              style={{
                flex: 1, background: "transparent", border: "none",
                color: s.enabled ? "#e2e8f0" : "#64748b",
                fontSize: 12, fontWeight: 600, outline: "none",
              }}
            />

            {/* Key count badge */}
            <span style={{
              fontSize: 10, color: "#60a5fa", background: "#1e3a5f",
              padding: "1px 6px", borderRadius: 10,
            }}>
              {(s.keys || []).length} keys
            </span>

            {/* Delete */}
            <button
              onClick={e => { e.stopPropagation(); removeSet(i); }}
              style={{
                background: "none", border: "none", color: "#64748b",
                cursor: "pointer", fontSize: 14, padding: "0 2px",
              }}
              title="Remove this output set"
            >×</button>

            {/* Expand chevron */}
            <span style={{ color: "#64748b", fontSize: 10 }}>
              {expandedIdx === i ? "▲" : "▼"}
            </span>
          </div>

          {/* Expanded: keys textarea */}
          {expandedIdx === i && (
            <div style={{ padding: "0 12px 12px" }}>
              <div style={{ fontSize: 10, color: "#8a9bb0", marginBottom: 4 }}>
                Paste question keys (comma, space, semicolon, or newline separated):
              </div>
              <textarea
                rows={4}
                value={keysToText(s.keys)}
                onChange={e => updateSet(i, 'keys', parseKeys(e.target.value))}
                placeholder="e.g. S1, S2, S3, Q4"
                style={{
                  width: "100%", boxSizing: "border-box",
                  background: "#0f172a", border: "1px solid #334155",
                  color: "#e2e8f0", borderRadius: 5,
                  padding: "6px 8px", fontSize: 11,
                  resize: "vertical", outline: "none", fontFamily: "monospace",
                }}
              />
              {(s.keys || []).length > 0 && (
                <div style={{ fontSize: 10, color: "#475569", marginTop: 4 }}>
                  {(s.keys || []).length} key(s): {(s.keys || []).slice(0, 8).join(", ")}
                  {(s.keys || []).length > 8 ? ` … +${(s.keys || []).length - 8} more` : ""}
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      <button
        onClick={addSet}
        style={{
          width: "100%", background: "#1e3a5f", border: "1px dashed #334155",
          color: "#60a5fa", borderRadius: 7, padding: "7px 0",
          fontSize: 12, cursor: "pointer", marginBottom: 10,
        }}
      >
        + Add Output Set
      </button>

      {outputSets.length > 1 && (
        <Toggle
          label="Separate Excel file per output set"
          value={separateExcelPerOutput}
          onChange={onChangeSeparate}
        />
      )}
      {separateExcelPerOutput && outputSets.length > 1 && (
        <div style={{ fontSize: 10, color: "#475569", marginTop: -4 }}>
          One Excel file per enabled output set, downloaded as a zip.
        </div>
      )}
      {!separateExcelPerOutput && outputSets.length > 1 && (
        <div style={{ fontSize: 10, color: "#475569" }}>
          All output sets in one Excel file — each set gets its own table of contents + data sheet.
        </div>
      )}
    </div>
  );
}

export default function RunTableSettingsModal({
  open, settings, onChange, onClose, hasWeights,
  outputSets, onChangeOutputSets,
}) {
  if (!open) return null;

  const set = (key, val) => onChange({ ...settings, [key]: val });

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.65)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: "#0f172a", border: "1px solid #1e2d45",
        borderRadius: 12, width: 460, maxWidth: "95vw",
        maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
        padding: "24px 28px",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#e2e8f0" }}>Run Tables Settings</div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>Formatting & display options</div>
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", color: "#64748b", fontSize: 18, cursor: "pointer", padding: "2px 6px" }}
          >×</button>
        </div>

        <div style={DIVIDER} />

        {/* ── Decimal Places ────────────────────────── */}
        <div style={SECTION}>
          <div style={SECTION_TITLE}>Decimal Places</div>

          <div style={SUB_LABEL}>General</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
            <NumInput label="Frequency" value={settings.freqDecimals} onChange={v => set("freqDecimals", v)} />
            <NumInput label="Percent (%)" value={settings.pctDecimals} onChange={v => set("pctDecimals", v)} />
            <NumInput label="Base" value={settings.baseDecimals} onChange={v => set("baseDecimals", v)} />
          </div>

          <div style={SUB_LABEL}>Statistics</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
            <NumInput label="Mean" value={settings.meanDecimals} onChange={v => set("meanDecimals", v)} />
            <NumInput label="Median" value={settings.medianDecimals} onChange={v => set("medianDecimals", v)} />
            <NumInput label="Std Dev" value={settings.stdDecimals} onChange={v => set("stdDecimals", v)} />
            <NumInput label="Std Error" value={settings.seDecimals} onChange={v => set("seDecimals", v)} />
            <NumInput label="Mode" value={settings.modeDecimals} onChange={v => set("modeDecimals", v)} />
            <NumInput label="Min" value={settings.minDecimals} onChange={v => set("minDecimals", v)} />
            <NumInput label="Max" value={settings.maxDecimals} onChange={v => set("maxDecimals", v)} />
            <NumInput label="Q1 (25th)" value={settings.q1Decimals} onChange={v => set("q1Decimals", v)} />
            <NumInput label="Q3 (75th)" value={settings.q3Decimals} onChange={v => set("q3Decimals", v)} />
          </div>

          <div style={SUB_LABEL}>Cell Value Precision (stored in cell)</div>
          <div style={{ fontSize: 10, color: "#475569", marginBottom: 8 }}>
            Controls the exact value written into each Excel cell (visible in formula bar). Independent of display decimals above.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <NumInput label="Frequency" value={settings.cellFreqDecimals} onChange={v => set("cellFreqDecimals", v)} max={10} />
            <NumInput label="Percent (%)" value={settings.cellPctDecimals} onChange={v => set("cellPctDecimals", v)} max={10} />
            <NumInput label="Base" value={settings.cellBaseDecimals} onChange={v => set("cellBaseDecimals", v)} max={10} />
          </div>
        </div>

        <div style={DIVIDER} />

        {/* ── Base Row Display ──────────────────────── */}
        <div style={SECTION}>
          <div style={SECTION_TITLE}>Base Rows</div>
          <Toggle label="Show Unweighted Base" value={settings.showUnweightedBase} onChange={v => set("showUnweightedBase", v)} />
          {hasWeights && (
            <Toggle label="Show Weighted Base" value={settings.showWeightedBase} onChange={v => set("showWeightedBase", v)} />
          )}
          <Toggle label="Show Base %" value={settings.showBasePct} onChange={v => set("showBasePct", v)} />
        </div>

        <div style={DIVIDER} />

        {/* ── Numeric Display ───────────────────────── */}
        <div style={SECTION}>
          <div style={SECTION_TITLE}>Numeric Questions</div>
          <Toggle
            label="Hide Auto Freq (Numeric types)"
            value={settings.hideAutofreq || false}
            onChange={v => set("hideAutofreq", v)}
          />
          {settings.hideAutofreq && (
            <div style={{ fontSize: 10, color: "#475569", marginTop: -4, marginBottom: 6 }}>
              Raw frequency rows are hidden for Numeric, Numeric2DGrid and Numeric3DGrid questions.
              Only nets, stats (mean, median, etc.) and base rows are exported.
            </div>
          )}
        </div>

        <div style={DIVIDER} />

        {/* ── Global Row Filter ─────────────────────────────── */}
        <div style={SECTION}>
          <div style={SECTION_TITLE}>Global Row Filter</div>
          <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8 }}>
            Enter filter logic separated by <strong style={{ color: "#93c5fd" }}>;</strong> for multiple outputs.
            Leave blank to export all respondents (ALL).
          </div>
          <input
            type="text"
            placeholder="e.g. S1=1;S1=2  or  ALL;S1=1"
            value={settings.globalFilter || ''}
            onChange={e => set("globalFilter", e.target.value)}
            style={{
              width: "100%", boxSizing: "border-box",
              background: "#0f172a", border: "1px solid #334155",
              color: "#e2e8f0", borderRadius: 5,
              padding: "6px 10px", fontSize: 12, outline: "none",
            }}
          />
          <div style={{ fontSize: 10, color: "#475569", marginTop: 5, marginBottom: 12 }}>
            Each term creates a separate sheet with its own Table of Contents.
          </div>
          <Toggle
            label="Separate Excel file per filter"
            value={settings.separateExcelPerFilter || false}
            onChange={v => set("separateExcelPerFilter", v)}
          />
          {settings.separateExcelPerFilter && (
            <div style={{ fontSize: 10, color: "#475569", marginTop: -4 }}>
              Multiple Excel files will be zipped and downloaded together.
            </div>
          )}
        </div>

        <div style={DIVIDER} />

        {/* ── Bilendi Options ──────────────────────────────── */}
        <div style={SECTION}>
          <div style={SECTION_TITLE}>Bilendi Options</div>
          <Toggle
            label="Show Total each group"
            value={settings.showTotalEachGroup || false}
            onChange={v => set("showTotalEachGroup", v)}
          />
          {settings.showTotalEachGroup && (
            <div style={{ fontSize: 10, color: "#475569", marginTop: -4, marginBottom: 6 }}>
              Prepends a Total column (letter A) to each banner group. Letters restart from A per group; sig test letters update accordingly.
            </div>
          )}
        </div>

        <div style={DIVIDER} />

        {/* ── Output Sets ───────────────────────────────────── */}
        <OutputSetsSection
          outputSets={outputSets || []}
          onChangeOutputSets={onChangeOutputSets}
          separateExcelPerOutput={settings.separateExcelPerOutput || false}
          onChangeSeparate={v => set("separateExcelPerOutput", v)}
        />

        <div style={DIVIDER} />

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{
              background: "linear-gradient(135deg,#1d4ed8,#2563eb)",
              color: "#fff", border: "none", borderRadius: 7,
              padding: "8px 22px", fontSize: 13, fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
